/**
 * SbomViewer — visualizza il contenuto del SBOM (export CycloneDX da DT) di un BUNDLE (foglia DT):
 * riepilogo + tabella componenti (nome, versione, tipo, purl) + toggle "JSON grezzo".
 * Fetch lazy: il chiamante monta il componente solo quando l'utente apre la vista.
 *
 * Slice 7d: la granularità è il bundle (componentId), non la release — i componenti/finding
 * vivono sulla foglia DT.
 */

import { useState } from 'react';
import { useSbomContent } from './hooks.js';
import { componentLabel } from './sbomDiff.js';
import { ApiError } from '../../api/client.js';
import styles from './Products.module.css';

export function SbomViewer({
  productId,
  version,
  componentId,
}: {
  productId: string;
  version: string;
  componentId: number;
}) {
  const { data: bom, isLoading, error } = useSbomContent(productId, version, componentId);
  const [showRaw, setShowRaw] = useState(false);
  const tid = componentId;

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid={`sbom-viewer-loading-${tid}`}>
        Caricamento SBOM…
      </p>
    );
  }

  if (error) {
    // 404 = bundle senza SBOM caricato (stato valido, non un errore tecnico).
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <p
        className={is404 ? styles.emptyState : styles.errorMsg}
        data-testid={`sbom-viewer-error-${tid}`}
      >
        {is404
          ? 'Nessun SBOM disponibile per questo bundle.'
          : error instanceof Error
            ? error.message
            : 'Errore nel caricamento del SBOM.'}
      </p>
    );
  }

  if (!bom) return null;

  const components = bom.components ?? [];

  return (
    <div className={styles.sbomViewer} data-testid={`sbom-viewer-${tid}`}>
      <div className={styles.sbomSummary}>
        <span className={styles.badge}>{bom.bomFormat ?? 'BOM'}</span>
        {bom.specVersion && <span className={styles.badge}>spec {bom.specVersion}</span>}
        <span data-testid={`sbom-component-count-${tid}`}>{components.length} componenti</span>
        <button
          type="button"
          className={`${styles.btnSecondary} ${styles.btnSmall}`}
          onClick={() => setShowRaw((v) => !v)}
          data-testid={`sbom-toggle-raw-${tid}`}
        >
          {showRaw ? 'Vista tabellare' : 'JSON grezzo'}
        </button>
      </div>

      {showRaw ? (
        <pre className={styles.sbomRaw} data-testid={`sbom-raw-${tid}`}>
          {JSON.stringify(bom, null, 2)}
        </pre>
      ) : components.length === 0 ? (
        <p className={styles.emptyState}>Nessun componente nel SBOM.</p>
      ) : (
        <table className={styles.table} data-testid={`sbom-components-${tid}`}>
          <thead>
            <tr>
              <th>Componente</th>
              <th>Versione</th>
              <th>Tipo</th>
              <th>PURL</th>
            </tr>
          </thead>
          <tbody>
            {components.map((c, i) => (
              <tr key={c.purl ?? `${componentLabel(c)}-${i}`}>
                <td>{componentLabel(c)}</td>
                <td>{c.version ?? '—'}</td>
                <td>{c.type ?? '—'}</td>
                <td>
                  <code className={styles.purlCell}>{c.purl ?? '—'}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
